import { formatDateNumerically } from "@vyne-shared/utils/date";
import type { StripeAccountStatusResult } from "api/responses";

type StripeAccountStatus = "success" | "info" | "warning" | "error";

interface StripeRequirementsDetails {
    readonly dueDate: string;
    readonly stripeErrorReasons: string[];
}

interface StripeNoticeStatus extends StripeRequirementsDetails {
    readonly status: StripeAccountStatus;
}

/** Determines the status of the Stripe account and the fields that need to be updated. */
export const buildStripeNoticeStatus = (data: StripeAccountStatusResult | undefined): StripeNoticeStatus => {
    const stripeErrorReasons: string[] = [];
    const dueDate = "";
    let status: StripeAccountStatus = "success";

    if (!data) {
        return { status, dueDate, stripeErrorReasons };
    }

    // if there are past due requirements, show an error
    if (!["Proceed", "Wait", "WaitForProceed"].includes(data.status)) {
        return { ...getRequirementsDetails(data.requirements), status: "error" };
    }

    // if there are currently due requirements, show a warning
    else if (data.requirements && data.requirements.errors.length > 0) {
        if (data.requirements.errors.some((error) => error.severity === "Past")) {
            status = "error";
        } else if (data.requirements.errors.some((error) => error.severity === "Currently")) {
            status = "warning";
        } else {
            status = "info";
        }

        return {
            ...getRequirementsDetails(data.requirements),
            status,
        };
    }

    return { status, dueDate, stripeErrorReasons };
};

const getRequirementsDetails = (
    requirements: StripeAccountStatusResult["requirements"],
): StripeRequirementsDetails => ({
    dueDate: requirements?.currentDeadline ? formatDateNumerically(new Date(requirements.currentDeadline)) : "",
    stripeErrorReasons: requirements?.errors.map((error) => error.reason) ?? [],
});
